import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, ClipboardList, GitPullRequest, Inbox, Sparkles, TrendingUp, Wrench } from "lucide-react";
import { api } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";

const cards = [
  { key: "incidents",        label: "Incidents",        icon: AlertTriangle, link: "/incidents",       gradient: "bg-card-incidents", chip: "from-orange-400 to-orange-600" },
  { key: "service_requests", label: "Service Requests", icon: ClipboardList, link: "/service-requests", gradient: "bg-card-sr",        chip: "from-sky-400 to-cyan-600" },
  { key: "change_requests",  label: "Change Requests",  icon: GitPullRequest,link: "/change-requests",  gradient: "bg-card-cr",        chip: "from-violet-400 to-fuchsia-600" },
  { key: "problems",         label: "Problems",         icon: Wrench,        link: "/problems",         gradient: "bg-card-problems",  chip: "from-pink-400 to-rose-600" },
];

export default function Dashboard() {
  const { user, hasRole } = useAuth();
  const [stats, setStats] = useState(null);
  const [overdueGroups, setOverdueGroups] = useState([]); // v2.0 #12
  const [unassigned, setUnassigned] = useState({ total: 0, by_kind: {} }); // v2.4

  useEffect(() => {
    api.get("/api/dashboard/stats").then((r) => setStats(r.data));
    api.get("/api/dashboard/overdue").then((r) => setOverdueGroups(r.data.groups || [])).catch(() => {});
    api.get("/api/dashboard/unassigned").then((r) => setUnassigned(r.data || { total: 0, by_kind: {} })).catch(() => {});
  }, []);

  const overdueCount = overdueGroups.reduce((acc, g) => acc + (g.items?.length || 0), 0);
  const canSeeUnassigned = hasRole("admin", "it_manager", "it_team", "it_security");

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Welcome banner */}
      <div className="relative overflow-hidden rounded-2xl bg-brand-gradient p-6 text-white shadow-elevated">
        <div className="absolute inset-0 bg-hero-mesh opacity-25 animate-mesh-drift pointer-events-none" />
        <div className="relative flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 text-emerald-100/90 text-xs uppercase tracking-widest mb-1">
              <Sparkles size={14} /> Welcome back
            </div>
            <h1 className="text-3xl font-bold tracking-tight">{user?.full_name}</h1>
            <p className="text-emerald-100/90 text-sm mt-1">
              You're signed in as <span className="font-semibold text-white">{user?.role}</span> at <span className="font-semibold text-white">{user?.plant || "—"}</span>.
            </p>
          </div>
          {hasRole("admin", "it_manager") && (
            <button onClick={async () => { await api.post("/api/sla/scan"); alert("SLA scan complete"); }}
              className="px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 backdrop-blur-sm ring-1 ring-white/30 text-sm font-medium transition">
              Run SLA scan
            </button>
          )}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(({ key, label, icon: Icon, link, gradient }) => {
          const total = stats?.totals?.[key] ?? "—";
          const open = stats?.open?.[key] ?? 0;
          const overdue = stats?.overdue?.[key] ?? 0;
          return (
            <Link key={key} to={link} className={`relative overflow-hidden rounded-2xl p-5 text-white shadow-soft card-hover ${gradient}`}>
              <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-white/10" />
              <div className="absolute -right-12 -bottom-10 w-32 h-32 rounded-full bg-white/5" />

              <div className="relative flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/85">{label}</div>
                  <div className="text-4xl font-extrabold mt-2 tabular-nums">{total}</div>
                  <div className="text-xs mt-2 flex items-center gap-2">
                    <span className="px-1.5 py-0.5 rounded bg-white/20 backdrop-blur-sm">{open} open</span>
                    {overdue > 0 && (
                      <span className="px-1.5 py-0.5 rounded bg-red-900/40 ring-1 ring-red-200/40 font-semibold">{overdue} overdue</span>
                    )}
                  </div>
                </div>
                <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur-sm ring-1 ring-white/25 flex items-center justify-center">
                  <Icon size={22} />
                </div>
              </div>

              <div className="relative mt-4 text-xs text-white/80 flex items-center gap-1 group-hover:gap-2 transition-all">
                View all <ArrowRight size={12} />
              </div>
            </Link>
          );
        })}
      </div>

      {/* v2.4: Unassigned tickets — every open ticket waiting for triage */}
      {canSeeUnassigned && unassigned.total > 0 && (
        <div className="card overflow-hidden">
          <div className="px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Inbox size={16} />
              <span className="font-semibold">Unassigned tickets · {unassigned.total} waiting for triage</span>
            </div>
            <span className="text-xs opacity-90">Awaiting assignment to an IT team member</span>
          </div>
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { kind: "incident", label: "Incidents", path: "/incidents", numField: "number", accent: "from-orange-400 to-orange-600" },
              { kind: "service_request", label: "Service Requests", path: "/service-requests", numField: "number", accent: "from-sky-400 to-cyan-600" },
              { kind: "change_request", label: "Change Requests", path: "/change-requests", numField: "number", accent: "from-violet-400 to-fuchsia-600" },
              { kind: "problem", label: "Problems", path: "/problems", numField: "number", accent: "from-pink-400 to-rose-600" },
            ].map(({ kind, label, path, accent }) => {
              const items = unassigned.by_kind?.[kind] || [];
              return (
                <div key={kind} className="rounded-lg border border-slate-200 overflow-hidden bg-white">
                  <div className={`px-3 py-2 bg-gradient-to-r ${accent} text-white text-xs font-semibold flex items-center justify-between`}>
                    <span>{label}</span>
                    <span className="bg-white/25 rounded-full px-2 py-0.5">{items.length}</span>
                  </div>
                  <div className="p-2 max-h-56 overflow-y-auto">
                    {items.length === 0 ? (
                      <div className="text-xs text-slate-400 italic py-2 text-center">All triaged.</div>
                    ) : (
                      <ul className="space-y-1.5">
                        {items.slice(0, 8).map((it) => (
                          <li key={`${it.type}-${it.id}`} className="text-xs">
                            <Link to={`${path}/${it.id}`} className="block hover:bg-slate-50 rounded px-1.5 py-1 -mx-1.5">
                              <div className="flex items-center gap-1.5">
                                <span className="font-mono text-[10px] text-slate-500 shrink-0">{it.number}</span>
                                {it.is_overdue && <span className="badge bg-red-100 text-red-700 text-[9px]">OVERDUE</span>}
                              </div>
                              <div className="text-slate-800 truncate">{it.title}</div>
                              <div className="text-[10px] text-slate-500">
                                {it.reporter_name || "—"}{it.plant ? ` · ${it.plant}` : ""}
                              </div>
                            </Link>
                          </li>
                        ))}
                        {items.length > 8 && (
                          <li className="text-[10px] text-slate-400 italic pl-1.5">
                            + {items.length - 8} more…
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* v2.0 #12: Overdue panel */}
      {overdueCount > 0 && (
        <Link to="/overdue" className="block card card-hover overflow-hidden">
          <div className="px-4 py-3 bg-gradient-to-r from-rose-500 to-red-600 text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} />
              <span className="font-semibold">Overdue Work · {overdueCount} ticket{overdueCount === 1 ? "" : "s"} past SLA</span>
            </div>
            <span className="text-xs underline-offset-2 hover:underline opacity-90 flex items-center gap-1">
              View all <ArrowRight size={12} />
            </span>
          </div>
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {overdueGroups.slice(0, 6).map((g) => (
              <div key={g.assignee} className="rounded-lg border border-slate-200 p-3 bg-rose-50/40">
                <div className="text-sm font-semibold text-slate-700">{g.assignee}</div>
                <div className="text-xs text-slate-500 mb-2">{g.items.length} overdue</div>
                <ul className="space-y-1 text-xs">
                  {g.items.slice(0, 3).map((it) => (
                    <li key={`${it.type}-${it.id}`} className="truncate">
                      <span className="font-mono text-slate-500">{it.number}</span> — <span className="text-slate-800">{it.title}</span>
                    </li>
                  ))}
                  {g.items.length > 3 && <li className="text-slate-400">+ {g.items.length - 3} more…</li>}
                </ul>
              </div>
            ))}
          </div>
        </Link>
      )}

      {/* Recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RecentList title="Recent Incidents"        icon={AlertTriangle}   accent="from-orange-400 to-orange-600" items={stats?.recent?.incidents}         link="/incidents"        numField="incident_number" />
        <RecentList title="Recent Service Requests" icon={ClipboardList}   accent="from-sky-400 to-cyan-600"      items={stats?.recent?.service_requests}  link="/service-requests" numField="request_number" />
        <RecentList title="Recent Change Requests"  icon={GitPullRequest}  accent="from-violet-400 to-fuchsia-600" items={stats?.recent?.change_requests}   link="/change-requests"  numField="change_number" />
        <RecentList title="Recent Problems"         icon={Wrench}          accent="from-pink-400 to-rose-600"     items={stats?.recent?.problems}          link="/problems"         numField="problem_number" />
      </div>
    </div>
  );
}

function RecentList({ title, icon: Icon, accent, items = [], link, numField }) {
  return (
    <div className="card card-hover overflow-hidden">
      <div className={`px-4 py-3 bg-gradient-to-r ${accent} text-white flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <Icon size={16} />
          <span className="font-semibold">{title}</span>
        </div>
        <Link to={link} className="text-xs underline-offset-2 hover:underline opacity-90">View all</Link>
      </div>
      <div className="p-4">
        {(items?.length ?? 0) === 0 ? (
          <div className="text-sm text-slate-500 py-3 text-center">Nothing recent.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {(items || []).map((it) => (
              <li key={it.id} className="py-2.5">
                <Link to={`${link}/${it.id}`} className="flex items-center justify-between gap-3 hover:bg-slate-50 -mx-2 px-2 rounded-md transition">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="font-mono text-[11px] text-slate-500 shrink-0">{it[numField]}</span>
                    <span className="truncate text-sm text-slate-800">{it.title}</span>
                  </div>
                  <span className={`badge status-${it.status}`}>{it.status.replace(/_/g, " ")}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
